import i18n from '@/utils/i18n';
import moment from 'moment-timezone';
import { Button } from '@/components/button';
import { Calendar } from '@usepepper/ui/drafts/calendar';
import { createStyles } from '@/contexts/theme-context';
import {
  MINUTE_INTERVAL,
  useNoteReminderForm,
} from '@/hooks/use-note-reminder-form';
import { NoteReminderFormProps } from './add-note-reminder-sheet-content';
import { TimePickerInput } from '@/components/time-picker/time-picker-input.web';
import { truncateTime } from '@/utils/date';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useEffect } from 'react';
import { View } from 'react-native';

export const AddNoteReminderModalContent: React.FC<NoteReminderFormProps> = ({
  onClose,
  noteReminder,
  onSave,
  onSaveEnabled,
  onChange,
  isDateDisabled,
}) => {
  const [styles] = useStyles();
  const { onDateChange, onTimeChange, minimumDate } = useNoteReminderForm(
    onChange,
    noteReminder,
  );

  useEffect(() => {
    // If no reminder is set and modal is opened, set the default date and time
    if (!noteReminder) {
      const currentTruncReminder = truncateTime(new Date(), MINUTE_INTERVAL);
      onChange({
        date: currentTruncReminder,
        time: currentTruncReminder,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCloseModal = () => {
    onChange(undefined); // Clear the reminder when closing the modal
    onClose();
  };

  const onTimeChangeWrapper = (time?: Date) => {
    if (!time) {
      onChange(undefined);
      return;
    }
    onTimeChange(time);
  };

  return (
    <View style={styles.popover}>
      <Calendar
        activeDate={noteReminder ? moment(noteReminder.date) : null}
        isDateDisabled={isDateDisabled}
        onDateChange={onDateChange}
      />
      <View style={styles.divider} />

      <View style={styles.timeContainer}>
        <Typography variant="heading-md">
          {i18n.t('new_note_reminder.time')}
        </Typography>

        <TimePickerInput
          date={noteReminder?.time}
          minimumDate={minimumDate}
          minuteInterval={MINUTE_INTERVAL}
          onChange={onTimeChangeWrapper}
          trackingData={null}
        />
      </View>

      <View style={[styles.row, styles.popoverActions]}>
        <Button
          onPress={onCloseModal}
          size="small"
          style={styles.popoverActionButton}
          trackingData={{ name: 'note_activity.close_reminder' }}
          type="tertiary"
        >
          {i18n.t('cancel')}
        </Button>
        <Button
          disabled={!onSaveEnabled}
          onPress={onSave}
          size="small"
          style={styles.popoverActionButton}
          trackingData={{ name: 'note_activity.save_reminder' }}
          type="primary"
        >
          {i18n.t('save')}
        </Button>
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  divider: {
    backgroundColor: theme.sk.color.border.primary,
    height: 1,
    marginHorizontal: 8,
    marginVertical: 16,
  },
  popover: {
    marginHorizontal: 8,
    marginVertical: 16,
  },
  popoverActionButton: {
    paddingHorizontal: 32,
  },
  popoverActions: {
    gap: 8,
    justifyContent: 'flex-end',
    marginTop: 16,
  },
  row: {
    flexDirection: 'row',
  },
  timeContainer: {
    gap: 8,
    marginTop: 8,
  },
}));
